import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import axios from "axios";

import Button from "../../components/Button/Button";
import Modal from "../../components/modal/Modal";
import BasicModal from "../../components/basic-modal/BasicModal";
import FloatingLabelInput from "../../components/FloatingLabelInput/FloatingLabelInput";
import DisplaySelect from "../../components/display-select/display-select";
import SingleSelect from "../../components/single-select/single-select";
import Loader from "../../components/loader/loader";
import { backendURL } from "../../utils/backed-url";
import { errorToastFunction, toastFunction } from "../../utils/toast";
import zonkeEnergyTCs from "../../utils/zonkeTCs";

const BankingApp = () => {
  const [packages, setPackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showTermsAndConditions, setShowTermsAndConditions] = useState(false);

  const onTermsAndConditions = () => setShowTermsAndConditions(true);
  const onTermsAndConditionsClose = () => setShowTermsAndConditions(false);

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    axios
      .get(`${backendURL}/v1/api/packages/`)
      .then(({ data }) => {
        setPackages(data.map((p) => ({ value: p.id, label: p.name })));
      })
      .catch(() => {
        setErrorMessage("Could not load packages. Please try again later.");
      })
      .finally(() => setPackagesLoading(false));
  }, []);

  const onSubmit = async (data) => {
    try {
      setSubmitting(true);
      setErrorMessage("");

      const { data: response } = await axios.post(
        `${backendURL}/v1/api/initiate-payment/`,
        {
          amount: parseInt(data.amount, 10),
          transaction_ref: `TR-${Date.now()}`,
          bank_ref: `BR-${Date.now()}`,
          client_id: parseInt(data.clientNumber, 10),
          package_id: parseInt(data.package_id?.value, 10),
          sa_id: data.saId,
        }
      );

      if (response.payment_url) {
        toastFunction("Payment initiated successfully");
        window.location.assign(response.payment_url);
      } else {
        errorToastFunction("Failed to initiate payment");
      }
    } catch (error) {
      const errMsg =
        error.response?.data?.error || "Failed to initiate payment";
      setErrorMessage(errMsg);
      errorToastFunction(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (packagesLoading) {
    return (
      <div className="signup-container">
        <div className="signup-card">
          <Loader sizeClass={"size--medium"} />
        </div>
      </div>
    );
  }

  return (
    <div className="signup-container">
      <Modal
        modal={showTermsAndConditions}
        onClose={onTermsAndConditionsClose}
        modalSize="large"
      >
        <BasicModal
          title={""}
          body={zonkeEnergyTCs}
          onCancel={onTermsAndConditionsClose}
          isHtml={true}
        />
      </Modal>

      <div className="signup-card">
        <div className="signup__logo-wrapper">
          <img
            className="signup__logo"
            src={"/images/zonkelogo.jpg"}
            alt="logo"
          />
        </div>
        <div className="signup__title">Pay With Your Banking App</div>
        <div className="signup__subtitle">
          Enter your details to pay through your bank
        </div>

        {errorMessage && (
          <div className="banking-app__error">{errorMessage}</div>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="form-group">
            <FloatingLabelInput
              type={"text"}
              className={""}
              disabled={false}
              name={"saId"}
              register={register}
              rules={{
                required: "ID number is required",
                pattern: {
                  value: /^\d{13}$/,
                  message: "ID number must be 13 digits",
                },
              }}
              placeholder={"ID Number"}
              errors={errors}
            />
          </div>

          <div className="form-group">
            <FloatingLabelInput
              type={"text"}
              className={""}
              disabled={false}
              name={"clientNumber"}
              register={register}
              rules={{
                required: "Client number is required",
                pattern: {
                  value: /^[0-9]+$/,
                  message: "Client number must contain only numbers",
                },
              }}
              placeholder={"Client Number"}
              errors={errors}
            />
          </div>

          <div className="form-group">
            <FloatingLabelInput
              type={"number"}
              className={""}
              disabled={false}
              name={"amount"}
              register={register}
              rules={{
                required: "Amount is required",
                min: {
                  value: 0.01,
                  message: "Minimum amount is R0.01",
                },
                max: {
                  value: 100000,
                  message: "Maximum amount is R100,000",
                },
              }}
              placeholder={"Amount"}
              errors={errors}
            />
          </div>

          <div className="input-container">
            <DisplaySelect
              placeholder={"Package"}
              name={"package_id"}
              errors={errors}
            >
              <Controller
                name={"package_id"}
                rules={{ required: "The Package field is required." }}
                control={control}
                render={({ field }) => (
                  <SingleSelect field={field} options={packages} />
                )}
              />
            </DisplaySelect>
          </div>

          <div className="banking-app__button-group">
            <Button
              type={"submit"}
              name={"Proceed to Payment"}
              className={"button--primary"}
              loading={submitting}
            />
          </div>

          <a href="#" className="zonke-terms" onClick={onTermsAndConditions}>
            Zonke Energy T&apos;s & C&apos;s
          </a>

          <div className="public-topup__login-link">
            <Link to="/" className="link">
              Back
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BankingApp;
